import * as React from "react";
import { useEffect, useState } from "react";
import {
  Grid,
  GridColumn as Column,
  GridNoRecords,
} from "@progress/kendo-react-grid";
import { toast } from "react-toastify";
import { ActiveCell, EditCommandCell, SignatureCell } from "../GridComponents";
import EditActivityTag from "./EditActivityTag";
import { updateActivityTag, useActivityTag } from "services/activityTagService";
import { useActiveWorkTypes } from "services/workTypeService";
import { GridLoader } from "components/GridLoader";

const getWorkTypeId = (item) => item?.workTypeId ?? null;

const sameId = (a, b) => String(a) === String(b);

const RowColorCell = (props) => {
  const { dataItem } = props;
  const colour = dataItem.colour;
  const hasColor = colour && colour !== "none" && colour.trim() !== "";

  return (
    <td className="text-center align-middle">
      {hasColor ? (
        <div
          style={{
            width: "20px",
            height: "20px",
            backgroundColor: colour,
            border: "1px solid #ccc",
            borderRadius: "3px",
            margin: "0 auto",
          }}
        />
      ) : (
        <div
          style={{
            width: "18px",
            height: "18px",
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "3px",
            position: "relative",
            margin: "0 auto",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              width: "22px",
              height: "2px",
              background: "#f8c2c2",
              transform: "translate(-50%, -50%) rotate(45deg)",
              borderRadius: "1px",
            }}
          />
        </div>
      )}
    </td>
  );
};

const WorkTypeNameCell = (props) => {
  const workTypeId = getWorkTypeId(props.dataItem);

  if (workTypeId == null) {
    return <td>All Work Types</td>;
  }

  return <td>{props.dataItem.workType ?? "-"}</td>;
};

const ActivityTagsList = ({ departmentID = 0, workTypeID = 0 }) => {
  const [openForm, setOpenForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [tagData, setTagData] = useState(null);

  const { data: gridData, loading } = useActivityTag();
  const { data: workTypes } = useActiveWorkTypes();

  useEffect(() => {
    const tags = Array.isArray(gridData) ? gridData : [];

    if (workTypeID > 0) {
      setTagData(
        tags.filter((tag) => {
          const workTypeId = getWorkTypeId(tag);
          return workTypeId != null && sameId(workTypeId, workTypeID);
        }),
      );
      return;
    }

    if (departmentID > 0) {
      const departmentWorkTypeIds = (workTypes || [])
        .filter((wt) => sameId(wt.departmentId, departmentID))
        .map((wt) => wt.workTypeID);

      setTagData(
        tags.filter((tag) => {
          const workTypeId = getWorkTypeId(tag);
          return (
            workTypeId != null &&
            departmentWorkTypeIds.some((id) => sameId(id, workTypeId))
          );
        }),
      );
      return;
    }

    setTagData(null);
  }, [gridData, departmentID, workTypeID, workTypes]);

  const displayData = tagData ?? gridData ?? [];

  const enterEdit = (item) => {
    setEditItem(item);
    setOpenForm(true);
  };

  const handleSubmit = async (values) => {
    const id = editItem?.tagId;
    if (id == null) return;

    try {
      const result = await updateActivityTag(id, values);
      if (result.success) {
        toast.success(`Activity Tag '${values?.name}' updated successfully`);
        setOpenForm(false);
        setEditItem(null);
      }
    } catch {
      // handled by service
    }
  };

  const MyEditCommandCell = (props) => (
    <EditCommandCell {...props} enterEdit={enterEdit} />
  );

  return (
    <div className="settings-grid h-100">
      <Grid
        data={displayData}
        dataItemKey="tagId"
        showLoader={loading}
        style={{ height: "100%", width: "max-content", maxWidth: "100%" }}
        loader={
          <div style={{ minHeight: "50vh" }}>
            <GridLoader />
          </div>
        }
      >
        <GridNoRecords>
          {loading ? (
            <span></span>
          ) : (
            displayData.length === 0 && "No records available"
          )}
        </GridNoRecords>
        <Column field="name" title=" " cell={SignatureCell} width="80" />
        <Column field="name" title="Name" />
        <Column cell={WorkTypeNameCell} title="Work Type" field="workTypeId" />
        <Column
          cell={RowColorCell}
          title="Row Color"
          field="colour"
          width="100"
          headerClassName="grid-header-center"
        />
        <Column
          cell={ActiveCell}
          title="Active"
          field="active"
          width="80"
          headerClassName="grid-header-center"
        />
        <Column title=" " cell={MyEditCommandCell} width="60" />
      </Grid>

      {openForm && (
        <EditActivityTag
          cancelEdit={() => {
            setOpenForm(false);
            setEditItem(null);
          }}
          onSubmit={handleSubmit}
          item={editItem}
        />
      )}
    </div>
  );
};

export default ActivityTagsList;
